import {
  APP_INITIALIZER,
  ApplicationConfig,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import localeNb from '@angular/common/locales/nb';
import moment, { Moment } from 'moment';
import { routes } from './app.routes';

import {
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptor,
} from '@azure/msal-angular';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { environment } from '../environments/environment';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ApiErrorInterceptor } from './features/interceptors/api-error.interceptor';
import { registerLocaleData } from '@angular/common';

registerLocaleData(localeNb);
moment.locale('nb');

class StrictNbMomentDateAdapter extends MomentDateAdapter {
  constructor() {
    super();
    this.setLocale('nb');
  }

  override parse(value: any, parseFormat: string | string[]): Moment | null {
    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (!trimmedValue) return null;

      // Accept only exact DD.MM.YYYY to avoid ambiguous manual parsing.
      if (!/^\d{2}\.\d{2}\.\d{4}$/.test(trimmedValue)) {
        return null;
      }

      const parsed = moment(trimmedValue, 'DD.MM.YYYY', 'nb', true);
      return parsed.isValid() ? parsed : null;
    }

    return super.parse(value, parseFormat);
  }
}

export function MSALInstanceFactory() {
  const lastPolicy = sessionStorage.getItem('activePolicy') || environment.azureB2C.policies.bankId.name;

  const tenant = environment.azureB2C.domain;
  const base = environment.azureB2C.knownAuthorities[0];
  const dynamicAuthority = `https://${base}/${tenant}/${lastPolicy}`;

  return new PublicClientApplication({
    auth: {
      clientId: environment.azureB2C.clientId,
      authority: dynamicAuthority,
      redirectUri: environment.azureB2C.redirectUri,
      knownAuthorities: environment.azureB2C.knownAuthorities,
      postLogoutRedirectUri: environment.azureB2C.postLogoutRedirectUri,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
    },
  });
}

export function msalAppInitFactory(msal: PublicClientApplication) {
  return async () => {
    await msal.initialize();

    const result = await msal.handleRedirectPromise().catch(() => null);

    if (result?.account) {
      msal.setActiveAccount(result.account);

      const tfp = (result.idTokenClaims as any)?.tfp;
      const acr = (result.idTokenClaims as any)?.acr;
      const policy = tfp || acr;
      if (policy) {
        sessionStorage.setItem('activePolicy', policy);
      }
      localStorage.removeItem('loginAttempted');
      localStorage.removeItem('authError');
      return;
    }
    const active = msal.getActiveAccount();
    const accounts = msal.getAllAccounts();

    const loginAttempted = localStorage.getItem('loginAttempted') === 'true';

    if (!active && accounts.length > 0) {
      const policy = sessionStorage.getItem('activePolicy')?.toLowerCase();

      const matchingAccount = policy
        ? accounts.find((acc) => {
            const accPolicy = ((acc.idTokenClaims as any)?.tfp || (acc.idTokenClaims as any)?.acr || '').toLowerCase();

            return accPolicy === policy;
          })
        : null;

      msal.setActiveAccount(matchingAccount ?? accounts[0]);

      localStorage.removeItem('loginAttempted');
      localStorage.removeItem('authError');
      return;
    }

    if (!active && accounts.length === 0 && loginAttempted) {
      localStorage.setItem('authError', 'login_not_completed');
      localStorage.removeItem('loginAttempted');

      if (window.location.pathname !== '/auth') {
        window.location.replace('/auth');
      }
    }

  };
}

export function MSALGuardConfigFactory() {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['openid', 'profile', 'phone', ...environment.azureB2C.msalScopes],
    },
    loginFailedRoute: '/auth',
  };
}

export function MSALInterceptorConfigFactory() {
  const contactScope = environment.azureB2C.contactReadWriteScope ?? environment.azureB2C.msalScopes[0];

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: new Map([
      [`${environment.apis.membershipApi}/contacts/`, [contactScope]],
      [`${environment.apis.membershipApi}/memberservices/`, environment.azureB2C.msalScopes],
      [`${environment.apis.membershipApi}/ps-minside/`, environment.azureB2C.msalScopes],
      [`${environment.apis.membershipApi}/organizationinfo/`, environment.azureB2C.msalScopes],
      [`${environment.apis.membershipApi}/Employer/`, environment.azureB2C.msalScopes],
      ['https://graph.microsoft.com/v1.0/me', ['User.Read']],
    ]),
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),

    { provide: HTTP_INTERCEPTORS, useClass: ApiErrorInterceptor, multi: true },

    { provide: LOCALE_ID, useValue: 'nb-NO' },
    { provide: MAT_DATE_LOCALE, useValue: 'nb' },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { strict: true, useUtc: false } },
    {
      provide: DateAdapter,
      useClass: StrictNbMomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: { dateInput: 'DD.MM.YYYY' },
        display: {
          dateInput: 'DD.MM.YYYY',
          monthYearLabel: 'MMMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      } as MatDateFormats,
    },

    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    {
      provide: APP_INITIALIZER,
      useFactory: msalAppInitFactory,
      deps: [MSAL_INSTANCE],
      multi: true,
    },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },

    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
  ],
};
